import { Checkbox, InputGroup, SelectMenu, Toggle } from '@components/base';
import { InputGroupProps } from '@components/base/forms/InputGroup/InputGroups';
import { classNames } from '@utils/classNames';
import React, { useCallback, useImperativeHandle, useState } from 'react';
import { FastField, useField } from 'formik';
import { EyeconClosedSVG } from '@components/common/svgs/EyeconClosedSVG';
import { EyeconSVG } from '@components/common/svgs/EyeconSVG';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { ExclamationCircleIcon } from '@heroicons/react/solid';
import TextareaAutosize from 'react-textarea-autosize';
import cs from 'classnames';

dayjs.extend(localeData);

export interface FormikWrapperProps extends InputGroupProps {
    bottomLabel?: string;
    errorMargin?: string;
    hideError?: boolean;
    initialValue?: string;
    hasError?: boolean;
    updateValueBlur?: (value: any) => void;
}

export type FormikWrapperRef = {
    resetInitial: () => void;
    setError: (msg: string) => void;
};

/**
 * I should more than likely separate out the FormikValues
 *
 */
export const FormikWrapped = React.forwardRef(function ForwardedFormik(
    {
        id = '',
        className,
        errorMargin = 'mb-6',
        hideError,
        bottomLabel,

        ...props
    }: FormikWrapperProps,
    ref: React.ForwardedRef<FormikWrapperRef>,
) {
    const [field, meta, helpers] = useField(id);
    const hasError = !!(meta.error && meta.touched);
    useImperativeHandle(
        ref,
        () => ({
            resetInitial: () => {
                helpers.setValue(meta.initialValue);
            },
            setError: (msg: string) => {
                helpers.setError(msg);
            },
        }),
        [helpers, meta, ref],
    );

    const inputClass = classNames(
        className && !hasError && !bottomLabel ? className : 'text-gray-700',
    );

    return (
        <React.Fragment>
            <InputGroup className={inputClass} {...field} {...props} hasError={hasError} />
            {hasError && !hideError && (
                <div className={`mt-1 text-red-600 text-xs whitespace-nowrap ${errorMargin}`}>
                    {meta.error}
                </div>
            )}
            {!hasError && bottomLabel && (
                <div className={`mt-1 text-gray-500 text-xs whitespace-nowrap ${errorMargin}`}>
                    {bottomLabel}
                </div>
            )}
        </React.Fragment>
    );
});

export function FormikTableInputs({
    id = '',
    updateValueBlur,
    className,
    errorMargin = 'mb-6',
    hasError,
    hideError,
    bottomLabel,
    ...props
}: FormikWrapperProps) {
    const inputClass = classNames(
        className && !hasError && !bottomLabel ? className : 'text-gray-700',
    );

    return (
        <FastField name={id}>
            {({ field }: any) => (
                <InputGroup className={inputClass} {...field} {...props} hasError={hasError} />
            )}
        </FastField>
    );
}

export const FormikPasswordToggle = React.forwardRef(
    ({ type, ...props }: FormikWrapperProps, ref: React.Ref<FormikWrapperRef>) => {
        const [showPassword, setShowPassword] = useState(false);
        const [_field, meta, _helpers] = useField(props.id ?? '');
        const Eyecon = (
            <div
                className="absolute right-0 top-0 bottom-0 flex items-center pr-4 cursor-pointer"
                onClick={() => setShowPassword((s) => !s)}>
                {showPassword ? (
                    <EyeconSVG className="fill-current text-gray-800" />
                ) : (
                    <EyeconClosedSVG className="fill-current text-gray-800" />
                )}
            </div>
        );

        return (
            <FormikWrapped
                ref={ref}
                type={showPassword ? 'text' : 'password'}
                {...props}
                rightIcon={Eyecon}
            />
        );
    },
);
FormikPasswordToggle.displayName = 'ToggleInputElement';

export function FormikDropdown({
    id,
    label,
    options = [],
    placeholder = '',
}: {
    id: string;
    options: any[];
    label?: string;
    placeholder?: string;
}) {
    const [_field, meta, helpers] = useField(id);

    return (
        <div className="mb-6">
            <SelectMenu
                selected={meta.value}
                setSelected={helpers.setValue}
                options={options}
                label={label}
                labelClassName="text-gray-500 text-base"
                className="text-passport"
                placeholder={placeholder}
            />
        </div>
    );
}

export const FormikWrappedTextarea = React.forwardRef(function ForwardedFormik(
    {
        id = '',
        className,
        errorMargin = 'mb-6',
        labelText = '',
        labelClassName = 'text-sm',
        placeholder = 'description',
        autoResize = false,
        ...props
    }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
        errorMargin?: string;
        labelText?: string;
        labelClassName?: string;
        autoResize?: boolean;
    },
    ref: React.ForwardedRef<any>,
) {
    const [field, meta, helpers] = useField(id);
    const hasError = !!(meta.error && meta.touched);
    const classNames = cs(
        'w-full border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-600 p-4 disabled:bg-gray-50',
        className,
    );

    return (
        <React.Fragment>
            <label className={labelClassName}>{labelText}</label>

            {autoResize ? (
                <TextareaAutosize
                    id={id}
                    {...field}
                    {...props}
                    placeholder={placeholder}
                    className={classNames}
                    minRows={props.rows ? props.rows : 3}
                    style={props.style as any}
                    cols={props.cols ? props.cols : 50}></TextareaAutosize>
            ) : (
                <textarea
                    id={id}
                    {...field}
                    {...props}
                    placeholder={placeholder}
                    className={classNames}
                    // rows={props.rows ?? 5}
                    cols={props.cols ? props.cols : 50}></textarea>
            )}
            {hasError && (
                <div className={`mt-1 text-red-600 text-sm ${errorMargin}`}>{meta.error}</div>
            )}
        </React.Fragment>
    );
});

export const FormikWrappedToggle = React.forwardRef(function ForwardedFormik(
    {
        id = '',
        onToggle,
        disabled,
    }: React.HTMLAttributes<HTMLTextAreaElement> & {
        labelText?: string;
        onToggle?: (val: boolean) => void;
        disabled?: boolean;
    },
    ref: React.ForwardedRef<any>,
) {
    const [_field, meta, helpers] = useField(id);

    const handleEnabled = useCallback(
        (val: boolean) => {
            onToggle?.(val);
            helpers.setValue(val);
        },
        [helpers.setValue, onToggle],
    );

    return (
        <React.Fragment>
            <Toggle enabled={meta.value} setEnabled={handleEnabled} disabled={disabled} />
        </React.Fragment>
    );
});

export const FormikWrappedCheckbox = React.forwardRef(function ForwardedFormik(
    {
        id = '',
        height,
        label,
        labelClassname,
        disabled,
    }: { id: string; height?: string; label: string; labelClassname?: string; disabled?: boolean },
    ref: React.ForwardedRef<any>,
) {
    const [_field, meta, helpers] = useField(id);
    const labelClassnames = cs({
        ['text-passport text-base hover:text-passport z-10']: !labelClassname,
        [`${labelClassname}`]: labelClassname,
    });
    return (
        <React.Fragment>
            <Checkbox
                ref={ref}
                height={height}
                srLabel={id}
                id={id}
                label={label}
                labelBold=""
                labelClassName={labelClassnames}
                name={id}
                checked={meta.value}
                checkboxClassName="items-center"
                setChecked={() => !disabled && helpers.setValue(!meta.value)}
            />
        </React.Fragment>
    );
});
